import React from "react";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { CloudinaryGif } from "../Screenshot";
import { Link as GatsbyLink } from "gatsby";
import { OdysseyUser, getConnectorsStatus } from "@utils";
import { useLocalStorage } from "react-use";

export const ConnectorsModal = ({
  isOpen,
  user,
}: {
  isOpen: boolean;
  user: OdysseyUser;
}) => {
  const [acknowledgedConnectorsTime, setLastAcknowledgedConnectorsTime] =
    useLocalStorage(`connectors-modal:acknowledged-time`, 0);

  const hasAcknowledgedConnectors = Boolean(acknowledgedConnectorsTime);

  const acknowledgeConnectors = () =>
    setLastAcknowledgedConnectorsTime(Date.now());
  const closeButtonColor = useColorModeValue("white", "current");

  const {
    hasCompletedConnectors = false,
    hasEnrolledInConnectors = false,
    enrolledAt,
  } = getConnectorsStatus(user) ?? {};

  /* Show modal if
    - The user has not completed connectors
    - Either: The user has not enrolled in connectors OR their enrollment is older than 30 days
    - The user has not acknowledged the connectors modal before
  */

  const timeEnrolledInConnectors = hasEnrolledInConnectors
    ? Date.now() - new Date(enrolledAt).getTime()
    : 0;

  const enrolledMoreThan30Days = timeEnrolledInConnectors > 2592000000;

  const isModalOpen =
    !hasCompletedConnectors &&
    (!hasEnrolledInConnectors || enrolledMoreThan30Days) &&
    !hasAcknowledgedConnectors &&
    isOpen;

  return (
    <Modal isOpen={isModalOpen} onClose={acknowledgeConnectors}>
      <ModalOverlay />
      <ModalContent
        aria-label="New course: Apollo Connectors"
        bgSize="cover"
        bgImage="https://res.cloudinary.com/apollographql/image/upload/v1741891879/odyssey/bg_y8ddzo.png"
        _light={{
          bgImage:
            "https://res.cloudinary.com/apollographql/image/upload/v1741899500/odyssey/ltbg-subtle_cfuiuo.png",
        }}
      >
        <ModalCloseButton
          // put close button on top of confetti canvas for mobile layout
          zIndex="1"
          color={{
            base: closeButtonColor,
            md: "current",
          }}
        />

        <ModalHeader pt="8" fontSize={{ base: "xl", md: "2xl" }}>
          <Box
            display="inline-block"
            paddingLeft="2"
            paddingRight="2"
            borderRadius="4"
            borderWidth="1px"
            borderColor="orange.300"
          >
            <Text
              color="orange.300"
              lineHeight="1.6em"
              fontSize="0.5em"
              textStyle="smallCaps"
            >
              NEW COURSE ALERT
            </Text>
          </Box>
          <Text fontSize="xxl" mt="2">
            Introducing <b>Apollo Connectors</b>
          </Text>
        </ModalHeader>
        <ModalBody>
          <Text mb="8">
            Connectors are the new and easy way to get started with GraphQL,
            using existing REST APIs.
          </Text>
          <CloudinaryGif
            src="v1742229233/connectors-gif_bmkxlq.mp4"
            alt="GIF showing a preview of the connectors course"
          />
          <Text mt="8">
            Say goodbye to GraphQL servers and resolvers—now, everything happens
            in the schema!
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button mr="3" onClick={acknowledgeConnectors}>
            Stay here
          </Button>
          <Button
            _light={{
              bgColor: "orange.200",
            }}
            _dark={{
              borderColor: "orange.300",
              borderWidth: "1px",
              color: "orange.300",
            }}
            onClick={() => {
              window.gtag?.("event", "navigate_connectors_from_modal");
              acknowledgeConnectors();
            }}
            as={GatsbyLink}
            to={`/connectors-intro-rest`}
          >
            Take the course
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
